import CategoryScoreCardHero from './CategoryScoreCardHero';
import { DATA_SOURCES } from '@/config/constants';
import DownloadTimeSeriesButton from 'components/Button/DownloadTimeSeriesButton';
import EntityBreadcrumbs from '../shared/entity/EntityBreadcrumbs';
import EntityChartDownload from '../shared/entity/EntityChartDownload';
import EntityCreators from '../shared/entity/EntityCreators';
import EntityFlavors from '../shared/entity/EntityFlavors';
import EntityGraphNav from '../shared/entity/EntityGraphNav';
import EntityPage from '../shared/entity/EntityPage';
import EntityRelatedTable from '../shared/entity/EntityRelatedTable';
import EntityTabs, { EntityTab } from '../shared/entity/EntityTabs';
import EntityTimeSeriesTab from '../shared/entity/EntityTimeSeriesTab';
import EntityTopPosts from '../shared/entity/EntityTopPosts';
import EntityTopTerms from '../shared/entity/EntityTopTerms';
import { hasFlavorsTab } from '../shared/entityPredicates';
import {
    RelatedBrandsSectionTable,
    RelatedCategoriesSectionTable,
} from 'components/DataVis/RelatedCategoriesTable';
import Stack from 'components/Stack';
import { SUMMARY_SPEC, scoreCardSummary } from './categorySummary';
import TikTokBreadCrumbs from 'components/Breadcrumbs/TikTokBreadCrumbs';
import TikTokGraphNav from 'components/DataVis/GraphNav/TikTokGraphNav';
import TikTokTimeSeries from 'components/DataVis/TikTokTimeSeries';
import { TIKTOK_METADATA_BY_KEY } from 'utils/metrics';
import { TopHashtagsSection } from 'components/TopKeywordsSection';
import { TopPostsSection } from 'components/Posts';
import { useCurrentCountry } from 'utils/hooks';
import useParentCategory from './useParentCategory';
import useTikTokEntity, {
    useTikTokSiblings,
} from '../shared/entity/useTikTokEntity';
import { useTrackDepthView } from 'utils/hooks/useTrackEntityView';
import WeeklyEntityChart, {
    weeklySeries,
} from '../shared/entity/WeeklyEntityChart';

const HERO_METRICS = [
    TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling,
    TIKTOK_METADATA_BY_KEY.non_cum_QoQ_rolling,
    TIKTOK_METADATA_BY_KEY.non_cum_MoM_rolling,
    TIKTOK_METADATA_BY_KEY.non_cum_WoW,
];

function TikTokCategoryThreePage({ categoryId, controller, tabs }) {
    const country = useCurrentCountry();
    const page = useTikTokEntity({
        entityType: 'categories',
        entityId: categoryId,
        country,
        controller,
        coverage: 'hintCat1',
    });
    const {
        entity: category,
        details,
        row,
        name,
        rowQuery,
        week,
        year,
        theme,
    } = page;

    const parentCategory = useParentCategory(page);
    const { siblings, ready } = useTikTokSiblings({
        parentCategory,
        country,
        week,
        year,
    });

    useTrackDepthView(DATA_SOURCES.TIKTOK, {
        depth: details?.depth,
        name,
        country,
    });

    const series = weeklySeries({ page, color: theme.tiktok_pink });

    const summary = scoreCardSummary({
        entity: category,
        parentCategory,
        siblings,
        ready,
        spec: SUMMARY_SPEC[DATA_SOURCES.TIKTOK],
        currentPage: {
            id: categoryId,
            name,
            non_cum_increase_YoY_rolling: row?.non_cum_increase_YoY_rolling,
        },
    });

    return (
        <EntityPage
            page={page}
            /* Stricter than the hook: a row can arrive without the views
               metric. */
            breadcrumbs={<EntityBreadcrumbs as={TikTokBreadCrumbs} />}
            sourceTabs={tabs}
            space="32px"
            tabs={
                <EntityTabs>
                    <EntityTab name="Related Categories">
                        <EntityRelatedTable
                            as={RelatedCategoriesSectionTable}
                            subject="categories"
                            icon="affinity"
                            title="Related Categories"
                            exportType="related-hashtag"
                            exportEntity={name || 'trend'}
                            sortParamKey="relatedSort"
                        />
                    </EntityTab>
                    <EntityTab name="Related Brands">
                        <EntityRelatedTable
                            as={RelatedBrandsSectionTable}
                            subject="brands"
                            icon="brand"
                            title="Related Brands"
                            exportType="brand-hashtag"
                            exportEntity={name || 'trend'}
                            sortParamKey="brandSort"
                        />
                    </EntityTab>
                    {hasFlavorsTab(page) && (
                        <EntityTab name="Flavors">
                            <EntityFlavors
                                dataSource="tiktok"
                                exportEntity={name || 'trend'}
                            />
                        </EntityTab>
                    )}
                    <EntityTab name="Time Series">
                        <EntityTimeSeriesTab
                            title="Time Series"
                            subtitle="How is the trend performing?"
                            tooltipKey="tikTokPredictionTitleHelp"
                            download={
                                <EntityChartDownload
                                    as={DownloadTimeSeriesButton}
                                    data={series}
                                    className="ml-auto"
                                    mixpanelMeta={{
                                        contentKey: 'TikTokTimeSeriesChart',
                                    }}
                                    exportEntity={name}
                                />
                            }
                        >
                            <WeeklyEntityChart
                                as={TikTokTimeSeries}
                                data={series}
                                cluster={row?.cluster}
                                isLoading={
                                    page.seriesQuery?.isLoading ||
                                    rowQuery.isLoading
                                }
                            />
                        </EntityTimeSeriesTab>
                    </EntityTab>
                    <EntityTab name="Creators">
                        <EntityCreators exportEntity={name || 'trend'} />
                    </EntityTab>
                </EntityTabs>
            }
        >
            <Stack $space="16px">
                <CategoryScoreCardHero
                    categoryId={categoryId}
                    name={name}
                    image={details?.image}
                    isLoading={rowQuery.isLoading}
                    volumeLabel="Views"
                    volume={row?.non_cum_avg_weekly_views}
                    metrics={row || {}}
                    metricsList={HERO_METRICS}
                    defaultMetric={TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling}
                    attributionProps={{ week, year }}
                    cluster={row?.cluster}
                    {...summary}
                />
                <EntityGraphNav as={TikTokGraphNav} />
                <EntityTopTerms as={TopHashtagsSection} />
                <EntityTopPosts as={TopPostsSection} />
            </Stack>
        </EntityPage>
    );
}

TikTokCategoryThreePage.displayName = 'TikTokCategoryThree';

export default TikTokCategoryThreePage;
